import type {
  ApiChat,
  ApiMessage,
  ApiNotifyPeerType,
  ApiPeer,
  ApiPeerNotifySettings,
} from '../../api/types';

import { isUserId } from '../../util/entities/ids';
import { omitUndefined } from '../../util/iteratees';
import { getServerTime } from '../../util/serverTime';
import { isChatChannel } from './chats';

export function getIsChatMuted(
  chat: ApiChat,
  notifyDefaults?: Record<ApiNotifyPeerType, ApiPeerNotifySettings>,
  notifyException?: ApiPeerNotifySettings,
) {
  const settings = getChatNotifySettings(chat, notifyDefaults, notifyException);
  if (!settings?.mutedUntil) return false;
  return getServerTime() < settings.mutedUntil;
}

export function getIsChatSilent(
  chat: ApiChat,
  notifyDefaults?: Record<ApiNotifyPeerType, ApiPeerNotifySettings>,
  notifyException?: ApiPeerNotifySettings,
) {
  const settings = getChatNotifySettings(chat, notifyDefaults, notifyException);
  if (!settings) return false;
  // `hasSound` is optional and only set when the user explicitly toggles sound off; treat a missing
  // value as "sound enabled" so notifications aren't silently muted when the backend omits the flag.
  return settings.hasSound === false;
}

export function getShouldShowMessagePreview(
  chat: ApiChat,
  notifyDefaults?: Record<ApiNotifyPeerType, ApiPeerNotifySettings>,
  notifyException?: ApiPeerNotifySettings,
) {
  const settings = getChatNotifySettings(chat, notifyDefaults, notifyException);
  return Boolean(settings?.shouldShowPreviews);
}

export function getShouldIgnoreNotificationMute(
  message: Partial<ApiMessage>,
  shouldNotifyAboutPinnedMessages: boolean,
) {
  if (!message.isMentioned) return false;

  const isPinMessage = message.content?.action?.type === 'pinMessage';
  if (isPinMessage) {
    return shouldNotifyAboutPinnedMessages && !message.isSilent;
  }

  return true;
}

export function getChatNotifySettings(
  chat: ApiChat,
  notifyDefaults?: Record<ApiNotifyPeerType, ApiPeerNotifySettings>,
  notifyException?: ApiPeerNotifySettings,
): ApiPeerNotifySettings | undefined {
  const defaults = notifyDefaults?.[getNotificationPeerType(chat)];
  // The chat carries its own mute state, which outlives a reload — the notify exception only holds
  // the mute made in this session (and never covers 1-1 chats at all, see `ApiChat.mutedUntil`)
  const chatSettings = chat.mutedUntil !== undefined ? { mutedUntil: chat.mutedUntil } : undefined;

  if (!notifyException && !defaults && !chatSettings) {
    return undefined;
  }

  return {
    ...defaults,
    ...chatSettings,
    ...(notifyException && omitUndefined(notifyException)),
  };
}

export function getNotificationPeerType(peer: ApiPeer): ApiNotifyPeerType {
  if (isUserId(peer.id)) {
    return 'users';
  }

  const chat = peer as ApiChat;
  return isChatChannel(chat) ? 'channels' : 'groups';
}
